import { UIElement } from '../ui-element';
import { Component } from '../../decorators/component.decorator';
import { Attribute } from '../../decorators/attribute.decorator';
import { Child } from '../../decorators/child.decorator';
import { closestByCond, setInnerText } from '../../global/ui-helpers';
import type { AttributeValue, ObservedAttributes } from '../ui-element.type';
import { NullOr } from '../../global/utils.type';
import styles from './ui-checkbox-account.css';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UICheckboxAccount
 * @element ui-checkbox-account
 * @classdesc Represents a class for <code>ui-checkbox-account</code> element.
 * Element is used in PFM project.
 * @property {string} [label=""] {@attr label} Specifies label.
 * @property {string|number} [number=""] {@attr number} Specifies account number.
 * @property {HTMLInputElement} input Shortcut to input element.
 * @slot
 * @example
 * <ui-checkbox-account label="John Dough" number="EE5122000001102304345">
 *   <input id="test-render-id-1" name="test-render-name-1" value="123" checked>
 * </ui-checkbox-account>
 */
@Component({ tag: 'ui-checkbox-account', styles })
export class UICheckboxAccount extends UIElement {
    /**
     * Slice account number for mobile: number of symbols at the beginning.
     * @type {number}
     */
    static readonly SLICE_ACCOUNT_START: number = 6;

    /**
     * Slice account number for mobile: number of symbols at the end.
     * @type {number}
     */
    static readonly SLICE_ACCOUNT_END: number = -4;

    @Attribute(String) label?: string;
    @Attribute(String) number?: string;

    @Child('.ui-checkbox-account__input') input!: HTMLInputElement;

    /**
     * @type {Array<string>}
     */
    static get observedAttributes(): ObservedAttributes {
        return ['label', 'number'];
    }

    get value(): string {
        return this.input.value;
    }

    set value(value) {
        const input = this.input;
        if (input.value === value) {
            return;
        }
        input.value = value;
    }

    get checked(): boolean {
        return this.input.checked;
    }

    set checked(value) {
        const input = this.input;
        if (input.checked === value) {
            return;
        }
        input.checked = value;
    }

    private mobileMediaQueryList!: MediaQueryList;

    /**
     * Check if a single grid item is used.
     * @returns {boolean}
     */
    isSingle(): boolean {
        return Boolean(
            closestByCond(this, (node) => {
                return (
                    !!(node as Element).tagName &&
                    (node as Element).matches('ui-grid:has(> :only-child)')
                );
            })
        );
    }

    /**
     * Return account number with *** for tablet and mobile.
     * @returns {string}
     */
    private renderAccountNumber(): string {
        const defaultAccountNumber = this.number || '';

        const isNonSingleMobileMedia =
            this.number &&
            this.mobileMediaQueryList &&
            this.mobileMediaQueryList.matches &&
            !this.isSingle();

        return isNonSingleMobileMedia
            ? this.number!.slice(0, UICheckboxAccount.SLICE_ACCOUNT_START) +
                  '***' +
                  this.number!.slice(UICheckboxAccount.SLICE_ACCOUNT_END)
            : defaultAccountNumber;
    }

    /**
     * Updates checkbox group.
     */
    updateCheckboxAccount(): void {
        const field = this.querySelector<HTMLInputElement>('input');

        if (this.isSingle()) {
            this.checked = true;
        }

        this.getLabelElement().setAttribute(
            'for',
            field?.getAttribute('id') || ''
        );
        setInnerText(this.getLabelText(), this.label || '');
        setInnerText(this.getLabelNumber(), this.renderAccountNumber());
    }

    /**
     * Get or create element for label wrapper
     * @returns {Element}
     */
    getLabelElement(): HTMLLabelElement {
        let el = this.querySelector<HTMLLabelElement>('label');
        if (!el) {
            el = document.createElement('label');
            this.appendChild(el);
        }
        return el;
    }

    /**
     * Get or create element for label text
     * @returns {Element}
     */
    getLabelText(): HTMLSpanElement {
        const container = this.querySelector<HTMLLabelElement>('label');
        let el = container?.querySelector<HTMLSpanElement>(
            '.ui-checkbox-account__label'
        );
        if (!el) {
            el = document.createElement('span');
            el.className = 'ui-checkbox-account__label';
            container?.appendChild(el);
        }
        return el;
    }

    /**
     * Get or create element for account number
     * @returns {Element}
     */
    getLabelNumber(): HTMLSpanElement {
        const container = this.querySelector<HTMLLabelElement>('label');
        let el = container?.querySelector<HTMLSpanElement>(
            '.ui-checkbox-account__number'
        );
        if (!el) {
            el = document.createElement('span');
            el.className = 'ui-checkbox-account__number';
            container?.appendChild(el);
        }
        return el;
    }

    /**
     * Handle event when media changed desktop to tablet and vice versa.
     */
    handleChangeAppearance = (): void => {
        setInnerText(this.getLabelNumber(), this.renderAccountNumber());
    };

    /**
     * @inheritDoc
     */
    observeAttributes(
        name: AttributeValue,
        oldValue: AttributeValue,
        newValue: NullOr<string>
    ): void {
        /* istanbul ignore if */
        if (!this.hydrated) {
            return;
        }

        switch (name) {
            case 'label':
                setInnerText(this.getLabelText(), this.label || '');
                break;
            case 'number':
                setInnerText(this.getLabelNumber(), this.renderAccountNumber());
                break;
            default:
                break;
        }
    }

    /**
     * @inheritDoc
     */
    override render(): void {
        const input =
            this.queryChildren('input')[0] ||
            this.createElement({
                tagName: 'input',
                attributes: { type: 'checkbox' },
            });

        const checkbox = input || this.input;
        checkbox.classList.add('ui-checkbox-account__input');

        this.mobileMediaQueryList = window.matchMedia('(max-width: 1023px)');
        this.appendChild(checkbox);
        this.updateCheckboxAccount();
    }

    /**
     * @inheritDoc
     */
    override hydrate(): void {
        this.mobileMediaQueryList = window.matchMedia('(max-width: 1023px)');
        this.handleChangeAppearance();
        this.mobileMediaQueryList.addListener(this.handleChangeAppearance);
    }
}
